import { FunctionComponent, memo, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Select from "../../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../../Components/Select/ISelectProps";
import { useAppDispatch, useAppSelector } from "../../../Redux/Store/Hooks";
import { IClassSelectionMapping } from "./IClassSelection";
import { DateCalendar, PickersDay, PickersDayProps } from "@mui/x-date-pickers";
import { Badge } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { ClassSelectionCreate_Update } from "../../../Redux/Slices/Booklet/Admin/ClassSelectionMaster/ClassSelectionMasterCreate_Update";
import { tutorsRead } from "../../../Redux/Slices/Booklet/Admin/Tutors/TutorsRead";
import { ClassSelectionSubjectRead } from "../../../Redux/Slices/Booklet/Admin/ClassSelectionMaster/ClassSelectionSubjectRead";
import { centreMasterRead } from "../../../Redux/Slices/Booklet/Admin/CentreMaster/CentreMatserRead";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

dayjs.extend(utc);
dayjs.extend(customParseFormat);

interface ClassSelectionFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: Partial<IClassSelectionMapping>;
  setInitialValues: React.Dispatch<
    React.SetStateAction<Partial<IClassSelectionMapping>>
  >;
  isEdit: boolean;
}

function ServerDay(
  props: PickersDayProps<Dayjs> & { selectedDays?: string[] }
) {
  const { day, selectedDays = [], ...other } = props;

  const isSelected = selectedDays.includes(day.format("YYYY-MM-DD"));

  return (
    <PickersDay
      {...other}
      day={day}
      selected={isSelected}
      sx={{
        // Apply custom selected style only when isSelected is true
        ...(isSelected && {
          backgroundColor: "#009ddf", // MUI primary blue
          color: "#ffff",
          fontWeight: 600,
          "&:hover": {
            backgroundColor: "#1565c0",
          },
          "&.Mui-selected": {
            backgroundColor: "#009ddf !important",
            color: "#ffff !important" ,
          },
        }),
        // Optional: Add subtle border or style for non-selected days if needed
        borderRadius: "50%",
        transition: "all 0.2s ease",
      }}
    />
  );
}



const ClassSelectionForm: FunctionComponent<ClassSelectionFormProps> = ({
  formRef,
  initialValues,
  setInitialValues,
  isEdit,
}) => {
  const dispatch = useAppDispatch();

  const { data: tutorList } = useAppSelector((state) => state.TutorsReadSlice);
  const { data: subjectList } = useAppSelector(
    (state) => state.ClassSelectionSubjectRead
  );
  const { data: centerList } = useAppSelector(
    (state) => state.CentreMasterReadSlice
  );

  const schema = z.object({
    class_selection_master_id: z.string(),
    tutor_id: z
      .number({
        required_error: "Please select a tutor",
        invalid_type_error: "Please select a tutor",
      })
      .refine((val) => val > 0, { message: "Please select a tutor" }),

    subject_id: z
      .number({
        required_error: "Please select a subject",
        invalid_type_error: "Please select a subject",
      })
      .refine((val) => val > 0, { message: "Please select a subject" }),

    center_id: z
      .string({
        required_error: "Please select a center",
        invalid_type_error: "Please select a center",
      })
      .min(1, "Please select a center"),

    status: z.boolean({
      required_error: "Please select a status",
    }),

    is_delete: z.boolean().default(false),

    dates: z
      .array(
        z.object({
          date: z.string(),
          day: z.string(),
        })
      )
      .min(1, "Please select at least one date"),
  });
  console.log(initialValues?.center_id);
  const methods = useForm<IClassSelectionMapping>({
    defaultValues: {
      class_selection_master_id:
        initialValues?.class_selection_master_id ?? "0",
      tutor_id: initialValues?.tutor_id ?? 0,
      subject_id: initialValues?.subject_id ?? 0,
      center_id: initialValues?.center_id ?? "",
      is_delete: initialValues?.is_delete ?? false,
      status: initialValues?.status ?? true,
      dates: Array.isArray(initialValues?.dates)
        ? initialValues.dates.map((d: any) => {
            // incoming API format: "27-11-25"
            const parsed = dayjs(
              d?.date,
              ["DD-MM-YY", "DD-MM-YYYY", "YYYY-MM-DD"],
              true
            );

            return {
              date: parsed.isValid() ? parsed.format("YYYY-MM-DD") : "",
              day: parsed.isValid() ? parsed.format("dddd") : "",
            };
          })
        : [],
    },
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<IClassSelectionMapping> = (values) => {
    const payload = {
      class_selection_master_id: values.class_selection_master_id ?? "0",
      tutor_id: values.tutor_id,
      subject_id: values.subject_id,
      center_id: values.center_id,
      is_delete: false,
      status: values.status ?? true,
      dates: values.dates.map((d) => ({
        date: dayjs(d.date, "YYYY-MM-DD")
          .utc()
          .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
        day: dayjs(d.date).format("dddd"),
      })),
    };

    console.log("✅ Final Payload =>", payload);
    dispatch(ClassSelectionCreate_Update(payload));
  };

  // 🔹 Load dropdown data
  useEffect(() => {
    dispatch(tutorsRead({ role: "TUTOR" }));
    dispatch(ClassSelectionSubjectRead());
    dispatch(centreMasterRead());
  }, [dispatch]);

  // 🔹 Handle initial date formatting correctly

  useEffect(() => {
    return () => setInitialValues({});
  }, [setInitialValues]);

  // Dropdown options
  const statusOptions: AutocompleteOption[] = [
    { label: "Active", value: true },
    { label: "Inactive", value: false },
  ];

  const tutorOptions: AutocompleteOption[] = Array.isArray(tutorList?.data)
    ? (tutorList?.data as any[]).map((t) => ({
        label: t.username,
        value: t.id,
      }))
    : [];

  const subjectOptions: AutocompleteOption[] = Array.isArray(subjectList?.data)
    ? (subjectList?.data as any[]).map((t) => ({
        label: t.name,
        value: t.id,
      }))
    : [];

  const centerOptions: AutocompleteOption[] = Array.isArray(centerList?.data)
    ? (centerList?.data as any[]).map((c) => ({
        label: `${c.center_name} (${c.center_id})`,
        value: c.center_id,
      }))
    : [];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormProvider {...methods}>
        <form
          ref={formRef}
          onSubmit={methods.handleSubmit(onSubmit)}
          className="form_double_input"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
            alignItems: "center",
          }}
        >
          {/* Tutor */}
          <Select
            name="tutor_id"
            label="Tutor"
            options={tutorOptions}
            value={
              tutorOptions.find(
                (option) => option.value === methods.getValues("tutor_id")
              ) ?? null
            }
            onChange={(_, value) =>
              methods.setValue(
                "tutor_id",
                (value as AutocompleteOption)?.value,
                {
                  shouldValidate: true,
                }
              )
            }
            error={!!methods.formState.errors.tutor_id}
            helperText={methods.formState.errors.tutor_id?.message}
          />

          {/* Subject */}
          <Select
            name="subject_id"
            label="Subject"
            options={subjectOptions}
            value={
              subjectOptions.find(
                (option) => option.value === methods.getValues("subject_id")
              ) ?? null
            }
            onChange={(_, value) =>
              methods.setValue(
                "subject_id",
                (value as AutocompleteOption)?.value,
                { shouldValidate: true }
              )
            }
            error={!!methods.formState.errors.subject_id}
            helperText={methods.formState.errors.subject_id?.message}
          />

          <Select
            name="center_id"
            label="Center"
            options={centerOptions}
            value={
              centerOptions.find(
                (option) => option.value === methods.getValues("center_id")
              ) ?? null
            }
            onChange={(_, value) =>
              methods.setValue(
                "center_id",
                (value as AutocompleteOption)?.value,
                {
                  shouldValidate: true,
                }
              )
            }
            error={!!methods.formState.errors.center_id}
            helperText={methods.formState.errors.center_id?.message}
          />

          <Select
            name="status"
            label="Status"
            options={statusOptions}
            value={
              statusOptions.find(
                (option) => option.value === methods.getValues("status")
              ) ?? null
            }
            onChange={(_, value) =>
              methods.setValue("status", (value as AutocompleteOption)?.value, {
                shouldValidate: true,
              })
            }
          />

          <div style={{ gridColumn: "1 / span 2" }}>
            <DateCalendar
              minDate={dayjs().startOf("day")}
              slots={{
                day: ServerDay,
              }}
              slotProps={{
                day: {
                  // This is the ownerState (day, outside, etc.)
                  // then add your custom prop safely
                  selectedDays:
                    methods.watch("dates")?.map((d: any) => d.date) || [],
                } as any, // temporary cast, or better — use proper typing below
              }}
              value={null}
              onChange={(newValue: Dayjs | null) => {
                if (!newValue?.isValid()) return;

                const dateStr = newValue.format("YYYY-MM-DD");
                const currentDates = methods.getValues("dates") || [];

                const exists = currentDates.some(
                  (d: any) => d.date === dateStr
                );

                const updatedDates = exists
                  ? currentDates.filter((d: any) => d.date !== dateStr)
                  : [
                      ...currentDates,
                      {
                        date: dateStr,
                        day: newValue.format("dddd"),
                      },
                    ];

                methods.setValue("dates", updatedDates, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
            />

            {/* Show selected dates as chips below calendar */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                marginTop: "16px",
                padding: "12px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
                minHeight: "60px",
              }}
            >
              {methods.watch("dates")?.length ? (
                methods.watch("dates").map((d: any, i: number) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px 16px",
                      background: "#E3F2FD",
                      color: "#1976D2",
                      borderRadius: "9999px",
                      fontSize: "14px",
                      fontWeight: 500,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    }}
                  >
                    <span>
                      {dayjs(d.date).format("DD-MM-YYYY")} ({d.day})
                    </span>
                    <HighlightOffIcon
                      sx={{
                        fontSize: 18,
                        cursor: "pointer",
                        color: "#d32f2f",
                        "&:hover": { color: "#b71c1c" },
                      }}
                      onClick={() => {
                        const updated = methods
                          .getValues("dates")
                          .filter((x: any) => x.date !== d.date);
                        methods.setValue("dates", updated, {
                          shouldValidate: true,
                        });
                      }}
                    />
                  </div>
                ))
              ) : (
                <span style={{ color: "#888", fontStyle: "italic" }}>
                  No dates selected yet
                </span>
              )}
            </div>

            {/* Validation error */}
            {methods.formState.errors.dates && (
              <p
                style={{
                  color: "#d32f2f",
                  fontSize: "0.75rem",
                  marginTop: "4px",
                }}
              >
                {methods.formState.errors.dates.message}
              </p>
            )}
          </div>

          {/* Dates Chip Row (Right Aligned)
          <div
            style={{
              gridColumn: "1 / span 2", // occupy full row
              display: "flex",
              justifyContent: "flex-start",
              flexWrap: "wrap",
              gap: "10px",
              marginTop: "10px",
            }}
          >
            {methods.watch("dates")?.map((d, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "6px 14px",
                  background: "#E3F2FD",
                  color: "#1976D2",
                  border: "1px solid #90CAF9",
                  borderRadius: "9999px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#BBDEFB";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#E3F2FD";
                }}
              >
                <span style={{ fontWeight: 500 }}>
                  {dayjs(d.date).format("DD-MM-YYYY")}
                </span>

                <span
                  style={{
                    cursor: "pointer",
                    color: "#D32F2F",
                    padding: "2px 6px",
                    borderRadius: "50%",
                    fontSize: "15px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease",
                  }}
                  onClick={() => {
                    const updated = methods
                      .getValues("dates")
                      .filter((x) => x.date !== d.date);

                    methods.setValue("dates", updated, {
                      shouldValidate: true,
                    });
                  }}
                >
                  <HighlightOffIcon />
                </span>
              </div>
            ))}
          </div> */}

          {/* Status */}
        </form>
      </FormProvider>
    </LocalizationProvider>
  );
};

export default memo(ClassSelectionForm);
